import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { requestsApi, adminApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useOpCo } from '../context/OpCoContext'
import { formatDate, formatTime, parseUTC } from '../utils/dateUtils'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import UserAvatar from '../components/UserAvatar'
import AnchoredPopover from '../components/AnchoredPopover'
import CollectionGrid from '../components/collection/CollectionGrid'
import { useRowGlow } from '../components/collection/cardInteractions'
import {
  FileText,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  Play
} from 'lucide-react'

function Approvals() {
  const { user: currentUser } = useAuth()
  const toast = useToast()
  const [requests, setRequests] = useState([])
  const [totalResults, setTotalResults] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [assigningId, setAssigningId] = useState(null)
  const [assignDropdown, setAssignDropdown] = useState(null) // request id with open dropdown
  // Assignee picker lists, keyed by the request's OpCo when OpCo workspaces
  // are on (a request can only be assigned to members of its own OpCo, and
  // the All OpCos view mixes OpCos); one org-wide list under 'org' when off.
  const [usersByKey, setUsersByKey] = useState({})
  const [loadingUsers, setLoadingUsers] = useState(false)
  const { enabled: opcoOn } = useOpCo()

  const isReader = isReadOnlyUser(currentUser)
  const isAdmin = currentUser?.role === 'admin' || isReader

  const totalPages = Math.ceil(totalResults / perPage)

  const fetchApprovals = useCallback(async (page, pageSize) => {
    try {
      setLoading(true)
      const params = {
        page,
        per_page: pageSize,
        status: 'pending_approval,in_review',
        // Requests whose template hands review to a connected application are
        // reviewed and published there. Listing them here would let two people
        // publish the same request from two apps, and the later publish would
        // silently discard the earlier corrections.
        exclude_external_review: true,
      }
      // Deliberately NOT scoped to assigned_to. "Send for Review" only assigns
      // a request when its template names an approver; with no approver set it
      // stays unassigned, and scoping this list to `assigned_to = me` made
      // those requests invisible to everyone except an admin — they were sent
      // for review and then vanished. The backend lets any user in the
      // organization self-assign a pending_approval request (POST
      // /requests/:id/assign) and publish it once assigned, so showing the
      // whole queue matches what a reviewer is actually allowed to act on.
      // The "Assigned To" column below says who already owns each one.
      const res = await requestsApi.list(params)
      setRequests(res.data.requests || [])
      setTotalResults(res.data.pagination?.total || 0)
      setError(null)
    } catch (err) {
      console.error('Error fetching approvals:', err)
      setError('Failed to load approvals')
    } finally {
      setLoading(false)
    }
  }, [currentUser, isReader])

  useEffect(() => {
    fetchApprovals(currentPage, perPage)
  }, [currentPage, perPage])

  // Close assign dropdown on click outside
  useEffect(() => {
    if (!assignDropdown) return
    const handleClick = () => setAssignDropdown(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [assignDropdown])

  const handleAssign = async (e, requestId) => {
    e.stopPropagation()
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    try {
      setAssigningId(requestId)
      await requestsApi.assign(requestId)
      toast.success('Review started')
      fetchApprovals(currentPage, perPage)
    } catch (err) {
      console.error('Error assigning request:', err)
      toast.error(err?.response?.data?.error || 'Failed to start review')
    } finally {
      setAssigningId(null)
    }
  }

  const handleAssignToUser = async (e, requestId, userId, username) => {
    e.stopPropagation()
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    try {
      setAssigningId(requestId)
      await requestsApi.assign(requestId, userId)
      toast.success(`Assigned to ${username}`)
      setAssignDropdown(null)
      fetchApprovals(currentPage, perPage)
    } catch (err) {
      console.error('Error assigning request:', err)
      toast.error(err?.response?.data?.error || 'Failed to assign request')
    } finally {
      setAssigningId(null)
    }
  }

  // Cache key of a request's assignee list: its OpCo (rows carry opco_id when
  // OpCo is on; 'none' = Unassigned), falling back to the request itself.
  const usersKeyFor = (req) => {
    if (!opcoOn) return 'org'
    if (req.opco_id === undefined) return `request:${req.id}`
    return `opco:${req.opco_id ?? 'none'}`
  }

  const openAssignDropdown = async (e, req) => {
    e.stopPropagation()
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    if (assignDropdown === req.id) {
      setAssignDropdown(null)
      return
    }
    setAssignDropdown(req.id)
    const key = usersKeyFor(req)
    if (!usersByKey[key]) {
      setLoadingUsers(true)
      try {
        const res = opcoOn
          ? await requestsApi.assignableUsers(req.id)
          : await adminApi.listUsers()
        const users = res.data.users || res.data || []
        setUsersByKey((prev) => ({ ...prev, [key]: users }))
      } catch (err) {
        toast.error('Failed to load users')
      } finally {
        setLoadingUsers(false)
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Approvals</h1>
              {!loading && (
                <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold text-navy-700 ring-1 ring-inset ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                  {totalResults}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Requests waiting for review and approval.</p>
          </div>
        </div>
        <button
          onClick={() => fetchApprovals(currentPage, perPage)}
          disabled={loading}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Approvals list */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-clip">
        {error ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-900/20 ring-1 ring-red-100 dark:ring-red-900/40">
              <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">Something went wrong</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{error}</p>
            <button
              onClick={() => fetchApprovals(currentPage, perPage)}
              className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </button>
          </div>
        ) : loading && requests.length === 0 ? (
          <ApprovalListSkeleton rows={6} />
        ) : requests.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-navy-50 to-navy-50 dark:from-navy-900/40 dark:to-navy-900/20 ring-1 ring-navy-100 dark:ring-navy-800">
              <CheckCircle className="h-8 w-8 text-navy-500 dark:text-navy-300" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">All caught up!</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">No requests are waiting for review right now.</p>
          </div>
        ) : (
          <>
            {/* Refetching over existing results: a sliver of progress, and the
                rows dim instead of blanking. */}
            <div className="relative h-0.5 overflow-hidden" aria-hidden="true">
              {loading && (
                <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />
              )}
            </div>

            <ApprovalListView busy={loading}>
              {/* Keyed on which requests are shown: a new page replays the entrance. */}
              <CollectionGrid
                key={requests.map(r => r.id).sort().join('-')}
                items={requests}
                getKey={r => r.id}
                enterFrom="translateY(10px)"
                className="tl-rows relative px-2 py-2 [&>div+div]:border-t [&>div+div]:border-gray-100 dark:[&>div+div]:border-gray-800/70"
              >
                {(req) => {
                  const usersKey = usersKeyFor(req)
                  return (
                    <ApprovalRow
                      request={req}
                      isReader={isReader}
                      isAdmin={isAdmin}
                      assigning={assigningId === req.id}
                      dropdownOpen={assignDropdown === req.id}
                      loadingUsers={loadingUsers}
                      users={usersByKey[usersKey]}
                      onStartReview={handleAssign}
                      onOpenAssign={openAssignDropdown}
                      onAssignTo={handleAssignToUser}
                    />
                  )
                }}
              </CollectionGrid>
            </ApprovalListView>

            {/* Pagination */}
            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>
                  {totalResults === 0
                    ? 'No results'
                    : `Showing ${(currentPage - 1) * perPage + 1}\u2013${Math.min(currentPage * perPage, totalResults)} of ${totalResults} result${totalResults !== 1 ? 's' : ''}`}
                </span>
                <span className="border-l border-gray-200 dark:border-gray-700 h-4" />
                <span className="flex items-center gap-1.5">
                  Rows per page
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded text-sm dark:bg-gray-800 dark:text-gray-200"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── List view ────────────────────────────────────────────────────────────────
// Same design language as the Requests list (Requests.jsx RequestListView):
// a CSS grid shared by the header and rows so the columns always line up, a
// sticky header, and tl-row hover treatment. Below the width the columns need,
// the list scrolls sideways and the header follows.

const APPROVAL_LIST_COLS = 'grid items-center gap-x-4 grid-cols-[minmax(14rem,2.6fr)_minmax(5.5rem,0.6fr)_minmax(6.5rem,0.8fr)_minmax(10rem,1fr)_minmax(10rem,1.1fr)_8.5rem]'
const APPROVAL_LIST_MIN_W = 'min-w-[62rem]'

const LIST_HEADER_CELL = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 whitespace-nowrap'

const STATUS_CONFIG = {
  pending_approval: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-300', dot: 'bg-purple-500', label: 'Waiting for Review', icon: 'text-purple-700 bg-purple-100 dark:text-purple-300 dark:bg-purple-950' },
  in_review: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-300', dot: 'bg-blue-500', label: 'In Review', icon: 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-950' },
}

function ApprovalListView({ busy, children }) {
  const headerTrackRef = useRef(null)

  // The header sits outside the sideways-scrolling rows so it can stick under
  // the top bar; when the rows scroll sideways, the header follows.
  const syncHeader = (e) => {
    const track = headerTrackRef.current
    if (track) track.style.transform = `translate3d(${-e.currentTarget.scrollLeft}px, 0, 0)`
  }

  return (
    <div aria-busy={busy || undefined} className={`transition-opacity duration-300 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
      <div className="sticky top-16 z-10 overflow-hidden border-b border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900">
        <div ref={headerTrackRef} className={`${APPROVAL_LIST_MIN_W} px-5 py-2.5 will-change-transform ${APPROVAL_LIST_COLS}`}>
          <span className={LIST_HEADER_CELL}>Request Name</span>
          <span className={LIST_HEADER_CELL}>Request</span>
          <span className={LIST_HEADER_CELL}>Created Date</span>
          <span className={LIST_HEADER_CELL}>Status</span>
          <span className={LIST_HEADER_CELL}>Assigned To</span>
          <span className={`${LIST_HEADER_CELL} justify-self-end`}>Action</span>
        </div>
      </div>
      <div className="overflow-x-auto" onScroll={syncHeader}>
        <div className={APPROVAL_LIST_MIN_W}>{children}</div>
      </div>
    </div>
  )
}

function ApprovalRow({
  request: req,
  isReader,
  isAdmin,
  assigning,
  dropdownOpen,
  loadingUsers,
  users,
  onStartReview,
  onOpenAssign,
  onAssignTo,
}) {
  const navigate = useNavigate()
  const { glowRef, ...glowHandlers } = useRowGlow()

  const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending_approval
  const documents = req.documents || []
  const documentCount = documents.length || req.document_count || 0
  const documentNames = documents.length > 0
    ? documents.map(d => d.filename || d.name || 'Unknown').join('\n')
    : (req.document_names || []).join('\n')
  const description = req.description || ''
  const assignedToName = req.assigned_to_name || 'Unknown'
  const detailHref = `/requests/${req.id}${isReader ? '?mode=view' : '?mode=edit'}`

  const handleRowClick = (e) => {
    if (e.target.closest('a') || e.target.closest('button')) return
    navigate(detailHref)
  }

  return (
    <article
      {...glowHandlers}
      onClick={handleRowClick}
      data-ready
      className={`tl-row group cursor-pointer rounded-xl px-3 py-3 ${APPROVAL_LIST_COLS}`}
    >
      <span aria-hidden="true" className="tl-row__bg">
        <span ref={glowRef} className="tl-row__glow" />
      </span>
      <span aria-hidden="true" className="tl-row__bar" />

      {/* Request Name */}
      <div className="min-w-0">
        <div className="flex items-center gap-3.5">
          <div
            className={`tl-row__icon w-10 h-10 rounded-xl flex items-center justify-center relative flex-shrink-0 cursor-default ${statusConfig.icon}`}
            title={documentCount > 0 ? documentNames : 'No documents'}
          >
            <FileText className="h-5 w-5" />
            {documentCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-navy-600 text-white text-[10px] font-semibold tabular-nums rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ring-2 ring-white dark:ring-gray-900">
                {documentCount}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <Link
              to={detailHref}
              className="block min-w-0 truncate rounded-sm text-[15px] font-semibold tracking-tight text-gray-900 transition-colors duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 group-hover:text-navy-700 dark:text-gray-50 dark:group-hover:text-white"
            >
              {req.title || req.email_subject || `Request #${String(req.id).substring(0, 8)}`}
            </Link>
            <p className={`mt-0.5 truncate text-[13px] ${description ? 'text-gray-500 dark:text-gray-400' : 'italic text-gray-400 dark:text-gray-500'}`} title={description || undefined}>
              {description || 'No description'}
            </p>
          </div>
        </div>
      </div>

      {/* Request */}
      <div>
        <span className="inline-flex rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">#{String(req.id).substring(0, 8)}</span>
      </div>

      {/* Created Date */}
      <div className="whitespace-nowrap">
        <p className="text-sm tabular-nums text-gray-700 dark:text-gray-300">{req.created_at ? formatDate(parseUTC(req.created_at)) : '-'}</p>
        <p className="mt-0.5 text-xs tabular-nums text-gray-400 dark:text-gray-500">{req.created_at ? formatTime(parseUTC(req.created_at)) : ''}</p>
      </div>

      {/* Status */}
      <div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.bg} ${statusConfig.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
          {statusConfig.label}
        </span>
      </div>

      {/* Assigned To */}
      <div className="min-w-0">
        {req.assigned_to ? (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="tl-row__avatar inline-flex flex-shrink-0">
              <UserAvatar user={{ id: req.assigned_to, username: assignedToName }} size="sm" className="!h-7 !w-7 !text-[11px] ring-2 ring-white dark:ring-gray-900" />
            </span>
            <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-300" title={assignedToName}>{assignedToName}</span>
          </div>
        ) : isAdmin ? (
          <div className="relative inline-block">
            <button
              type="button"
              onClick={(e) => onOpenAssign(e, req)}
              disabled={isReader}
              title={isReader ? READ_ONLY_MESSAGE : undefined}
              aria-expanded={dropdownOpen}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign
              <ChevronDown className={`h-3 w-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              // Rendered in a portal: list rows use content-visibility, which
              // would clip a dropdown drawn inside them. The probe sits one
              // menu-width (w-52) right of the button, so the menu's left edge
              // lines up with the button's.
              <AnchoredPopover
                anchorClassName="top-full left-52"
                onClick={(e) => e.stopPropagation()}
                className="w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto"
              >
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                ) : (
                  (users || []).filter(u => u.role !== 'sender').map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={(e) => onAssignTo(e, req.id, u.id, u.username || u.email)}
                      disabled={isReader || assigning}
                      title={isReader ? READ_ONLY_MESSAGE : undefined}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="font-medium">{u.username || u.email}</span>
                      <span className="text-[10px] ml-1.5 text-gray-400 capitalize">({u.role})</span>
                    </button>
                  ))
                )}
              </AnchoredPopover>
            )}
          </div>
        ) : (
          <span className="text-sm italic text-gray-400 dark:text-gray-500">Unassigned</span>
        )}
      </div>

      {/* Action */}
      <div className="flex justify-end">
        {req.status === 'pending_approval' ? (
          <button
            type="button"
            onClick={(e) => onStartReview(e, req.id)}
            disabled={isReader || assigning}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            // Visible at rest (it's the row's real action), and fills solid navy
            // with the row's hover, in step with the "Review" pill on rows
            // already in review.
            className="group/start relative inline-flex h-8 min-w-[7.75rem] items-center gap-2 rounded-lg bg-white pl-1 pr-3 text-xs font-semibold text-navy-700 shadow-[0_1px_2px_rgba(16,24,40,0.06)] ring-1 ring-inset ring-navy-200 transition-[background-color,color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:bg-navy-800 group-hover:text-white group-hover:ring-navy-800 group-hover:shadow-[0_6px_16px_-6px_rgb(var(--navy-800)/0.55)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 disabled:cursor-not-allowed disabled:opacity-50 disabled:group-hover:bg-white disabled:group-hover:text-navy-700 disabled:group-hover:ring-navy-200 disabled:group-hover:shadow-none dark:bg-navy-900/40 dark:text-navy-200 dark:ring-navy-700 dark:group-hover:bg-navy-600 dark:group-hover:ring-navy-600"
          >
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-navy-50 text-navy-700 ring-1 ring-inset ring-navy-100 transition-colors duration-300 group-hover:bg-white/15 group-hover:text-white group-hover:ring-white/20 dark:bg-navy-800 dark:text-navy-200 dark:ring-navy-700">
              {assigning ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3 w-3 translate-x-px fill-current transition-transform duration-300 group-hover/start:translate-x-0.5" />
              )}
            </span>
            {assigning ? 'Starting…' : 'Start Review'}
          </button>
        ) : (
          <span aria-hidden="true" className="tl-open">
            <span className="tl-open__bg" />
            <span className="tl-open__label">Review</span>
            <ChevronRight className="tl-open__icon h-4 w-4" />
          </span>
        )}
      </div>
    </article>
  )
}

// Same grid as ApprovalListView, so every placeholder sits in its column.
function ApprovalListSkeleton({ rows }) {
  const headers = ['w-24', 'w-14', 'w-20', 'w-12', 'w-20']
  return (
    <div aria-busy="true" aria-label="Loading" className="overflow-x-auto">
      <div className={APPROVAL_LIST_MIN_W}>
        <div className={`border-b border-gray-100 bg-gray-50 px-5 py-2.5 dark:border-gray-800 dark:bg-gray-900 ${APPROVAL_LIST_COLS}`}>
          {headers.map((w, i) => <div key={i} className="flex h-[18px] items-center"><div className={`skeleton h-2.5 ${w}`} /></div>)}
          <div className="flex h-[18px] items-center justify-end"><div className="skeleton h-2.5 w-12" /></div>
        </div>
        <div className="divide-y divide-gray-100 px-2 py-2 dark:divide-gray-800/70">
          {[...Array(rows)].map((_, i) => (
            <div key={i} className={`px-3 py-3 ${APPROVAL_LIST_COLS}`} style={{ opacity: 1 - i * 0.09 }}>
              {/* Request Name */}
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="skeleton h-10 w-10 flex-shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1">
                  <div className="flex h-[22.5px] items-center"><div className="skeleton h-4 w-36" /></div>
                  <div className="mt-0.5 flex h-[19.5px] items-center"><div className="skeleton h-3 w-48 max-w-full" /></div>
                </div>
              </div>
              {/* Request */}
              <div className="skeleton h-5 w-16 rounded-md" />
              {/* Created Date */}
              <div>
                <div className="flex h-5 items-center"><div className="skeleton h-3.5 w-20" /></div>
                <div className="mt-0.5 flex h-4 items-center"><div className="skeleton h-3 w-10" /></div>
              </div>
              {/* Status */}
              <div className="skeleton h-6 w-28 rounded-full" />
              {/* Assigned To */}
              <div className="flex items-center gap-2.5">
                <div className="skeleton h-7 w-7 flex-shrink-0 rounded-full" />
                <div className="skeleton h-4 w-24" />
              </div>
              {/* Action */}
              <div className="flex justify-end"><div className="skeleton h-7 w-24 rounded-lg" /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Approvals
